import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Container,
  Stack,
  IconButton,
  Popover,
  Button,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  Tune
} from '@mui/icons-material';
import { useGetMyAgencies } from '@frontend/features/agencies/queries';
import { useGetAllTeams } from '@frontend/features/teams/queries';
import { useGetNeedAgentStats, useGetCasesByPeriods, useGetCasesResolvedByPeriods, useGetCasesNeedAgentByTimeOpen, useGetImportantNumbers, useGetCasesByDayOfWeek, useGetCasesByDayAndHour } from '@frontend/features/cases/queries';
import { OriginatedByChannel } from '@bandido/shared/types';
import { DayOfWeek } from '@frontend/features/dashboard/components/TrendsSection';
import {
  ImportantNumbersSection,
  NeedsAgentSection,
  AgentsStatusSection,
  TrendsSection,
  ComparativesSection
} from '@frontend/features/dashboard/components';

const DASHBOARD_FILTERS_KEY = 'dashboard-filters';

interface DashboardFilters {
  selectedAgencies: string[];
  selectedTeams: string[];
  selectedChannels: string[];
  startDate: string | null;
  selectedDayOfWeek: DayOfWeek | null;
}

const loadFiltersFromStorage = (): Partial<DashboardFilters> => {
  try {
    const stored = localStorage.getItem(DASHBOARD_FILTERS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Error loading dashboard filters from localStorage:', error);
    return {};
  }
};

const saveFiltersToStorage = (filters: DashboardFilters) => {
  try {
    localStorage.setItem(DASHBOARD_FILTERS_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn('Error saving dashboard filters to localStorage:', error);
  }
};

export const Dashboard = () => {
  const { t } = useTranslation();
  const { data: agencies } = useGetMyAgencies({});
  const { data: teams } = useGetAllTeams({
    pageSize: 50,
    currentPage: 1,
    search: '',
  });

  const savedFilters = loadFiltersFromStorage();
  const [selectedAgencies, setSelectedAgencies] = useState<string[]>(
    savedFilters.selectedAgencies || []
  );
  const [selectedTeams, setSelectedTeams] = useState<string[]>(
    savedFilters.selectedTeams || []
  );
  const [selectedChannels, setSelectedChannels] = useState<string[]>(
    savedFilters.selectedChannels || []
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(
    savedFilters.startDate ? dayjs(savedFilters.startDate) : dayjs().subtract(30, 'days')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    dayjs().add(1, 'day')
  );
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<DayOfWeek>(
    savedFilters.selectedDayOfWeek || DayOfWeek.MONDAY
  );

  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);
  const filtersOpen = Boolean(filtersAnchorEl);

  const updateFilters = () => {
    const filters: DashboardFilters = {
      selectedAgencies,
      selectedTeams,
      selectedChannels,
      startDate: startDate?.toISOString() || null,
      selectedDayOfWeek,
    };
    saveFiltersToStorage(filters);
  };

  useEffect(() => {
    updateFilters();
  }, [selectedAgencies, selectedTeams, selectedChannels, startDate, endDate, selectedDayOfWeek]);

  // Actualizar endDate automáticamente para que siempre sea "mañana"
  useEffect(() => {
    const updateEndDate = () => {
      const tomorrow = dayjs().add(1, 'day');
      setEndDate(tomorrow);
    };
    
    // Actualizar inmediatamente
    updateEndDate();
    
    // Actualizar cada hora para asegurar que siempre sea "mañana"
    const interval = setInterval(updateEndDate, 60 * 60 * 1000); // cada hora
    
    return () => clearInterval(interval);
  }, []); // Solo ejecutar una vez al montar el componente

  useEffect(() => {
    if (agencies && agencies.length > 0 && selectedAgencies.length === 0) {
      const validAgencyIds = agencies.map(agency => String(agency.id));
      setSelectedAgencies(validAgencyIds);
    }
  }, [agencies, selectedAgencies.length]);

  useEffect(() => {
    if (teams?.items && teams.items.length > 0 && selectedTeams.length === 0) {
      const validTeamIds = [
        ...teams.items.map(team => String(team.id)),
        'notselected'
      ];
      setSelectedTeams(validTeamIds);
    }
  }, [teams?.items, selectedTeams.length]);

  useEffect(() => {
    if (selectedChannels.length === 0) {
      const allChannelIds = [
        OriginatedByChannel.WHATSAPP,
        OriginatedByChannel.CALL,
        OriginatedByChannel.EMAIL,
        OriginatedByChannel.MANUAL,
        OriginatedByChannel.EXTERNAL_INTEGRATION,
        OriginatedByChannel.UPLOADED,
        OriginatedByChannel.SUPPORT_FORM,
      ];
      setSelectedChannels(allChannelIds);
    }
  }, []);
  useEffect(() => {
    if (agencies && agencies.length > 0) {
      const validAgencyIds = agencies.map(agency => String(agency.id));
      const filteredAgencies = selectedAgencies.filter(id => validAgencyIds.includes(id));
      if (filteredAgencies.length !== selectedAgencies.length) {
        setSelectedAgencies(filteredAgencies);
      }
    }
  }, [agencies]);

  useEffect(() => {
    if (teams?.items && teams.items.length > 0) {
      const validTeamIds = [
        ...teams.items.map(team => String(team.id)),
        'notselected'
      ];
      const filteredTeams = selectedTeams.filter(id => validTeamIds.includes(id));
      if (filteredTeams.length !== selectedTeams.length) {
        setSelectedTeams(filteredTeams);
      }
    }
  }, [teams?.items]);

  const handleFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
    setFiltersAnchorEl(event.currentTarget);
  };

  const handleFiltersClose = () => {
    setFiltersAnchorEl(null);
  };

  const handleClearFilters = () => {
    const defaultAgencies = agencies ? agencies.map(agency => String(agency.id)) : [];
    const defaultTeams = [
      ...(teams?.items ? teams.items.map(team => String(team.id)) : []),
      'notselected'
    ];
    const defaultChannels = [
      OriginatedByChannel.WHATSAPP,
      OriginatedByChannel.CALL,
      OriginatedByChannel.EMAIL,
      OriginatedByChannel.MANUAL,
      OriginatedByChannel.EXTERNAL_INTEGRATION,
      OriginatedByChannel.UPLOADED,
      OriginatedByChannel.SUPPORT_FORM,
    ];

    setSelectedAgencies(defaultAgencies);
    setSelectedTeams(defaultTeams);
    setSelectedChannels(defaultChannels);
    setStartDate(dayjs().subtract(30, 'days'));
    setEndDate(dayjs().add(1, 'day'));
    setSelectedDayOfWeek(DayOfWeek.MONDAY);
  };

  const handleRefreshImportantNumbers = () => {
    refetchImportantNumbers();
  };

  const handleRefreshComparatives = () => {
    refetchCasesByPeriods();
    refetchCasesResolvedByPeriods();
    refetchCasesNeedAgentByTimeOpen();
  };

  const handleRefreshTrends = () => {
    refetchCasesByDayOfWeek();
    refetchCasesByDayAndHour();
  };

  const handleRefreshNeedsAgent = () => {
    refetchNeedAgentStats();
  };

  const processedTeams = selectedTeams.map(id => {
    if (id === 'notselected') {
      return 'notselected';
    } else {
      return Number(id);
    }
  }) as (number | 'notselected')[];

  const { data: needAgentStats, refetch: refetchNeedAgentStats } = useGetNeedAgentStats({
    agencyIds: selectedAgencies.map(id => Number(id)),
    channels: selectedChannels as OriginatedByChannel[],
    teams: processedTeams,
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
    enabled: selectedAgencies.length > 0,
  });

  const { data: casesByPeriods, refetch: refetchCasesByPeriods } = useGetCasesByPeriods({
    agencyIds: selectedAgencies.map(id => Number(id)),
    channels: selectedChannels as OriginatedByChannel[],
    teams: processedTeams,
    enabled: selectedAgencies.length > 0,
  });

  const { data: casesResolvedByPeriods, refetch: refetchCasesResolvedByPeriods } = useGetCasesResolvedByPeriods({
    agencyIds: selectedAgencies.map(id => Number(id)),
    channels: selectedChannels as OriginatedByChannel[],
    teams: processedTeams,
    enabled: selectedAgencies.length > 0,
  });

  const { data: casesNeedAgentByTimeOpen, refetch: refetchCasesNeedAgentByTimeOpen } = useGetCasesNeedAgentByTimeOpen({
    agencyIds: selectedAgencies.map(id => Number(id)),
    channels: selectedChannels as OriginatedByChannel[],
    teams: processedTeams,
    enabled: selectedAgencies.length > 0,
  });

  const { data: importantNumbers, refetch: refetchImportantNumbers } = useGetImportantNumbers({
    agencyIds: selectedAgencies.map(id => Number(id)),
    enabled: selectedAgencies.length > 0,
  });

  const startDateString = startDate?.toISOString() || '';
  const endDateString = endDate?.toISOString() || '';

  const { data: casesByDayOfWeek, refetch: refetchCasesByDayOfWeek } = useGetCasesByDayOfWeek({
    agencyIds: selectedAgencies.map(id => Number(id)),
    startDate: startDateString,
    endDate: endDateString,
    enabled: selectedAgencies.length > 0 && startDateString !== '' && endDateString !== '',
  });

  const { data: casesByDayAndHour, refetch: refetchCasesByDayAndHour } = useGetCasesByDayAndHour({
    agencyIds: selectedAgencies.map(id => Number(id)),
    startDate: startDateString,
    endDate: endDateString,
    dayOfWeek: selectedDayOfWeek,
    enabled: selectedAgencies.length > 0 && startDateString !== '' && endDateString !== '',
  });

  const agencyOptions = agencies || [];

  const teamOptions = [
    ...(teams?.items || []),
    { id: 'notselected' as any, name: t('dashboard.filters.withoutTeam') }
  ];

  const channelOptions = [
    {
      id: OriginatedByChannel.WHATSAPP,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.WHATSAPP}`),
    },
    {
      id: OriginatedByChannel.CALL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.CALL}`),
    },
    {
      id: OriginatedByChannel.EMAIL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.EMAIL}`),
    },
    {
      id: OriginatedByChannel.MANUAL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.MANUAL}`),
    },
    {
      id: OriginatedByChannel.EXTERNAL_INTEGRATION,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.EXTERNAL_INTEGRATION}`),
    },
    {
      id: OriginatedByChannel.UPLOADED,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.UPLOADED}`),
    },
    {
      id: OriginatedByChannel.SUPPORT_FORM,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.SUPPORT_FORM}`),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t('dashboard.title')}
        </Typography>

        <IconButton
          onClick={handleFiltersClick}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <Tune />
        </IconButton>
      </Box>

      <Popover
        open={filtersOpen}
        anchorEl={filtersAnchorEl}
        onClose={handleFiltersClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 3, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            {t('common.filters')}
          </Typography>

          <Stack spacing={2}>
            <Autocomplete
              multiple
              size="small"
              limitTags={2}
              options={agencyOptions}
              getOptionLabel={(option) => option.name}
              value={agencyOptions.filter((option) => selectedAgencies.includes(String(option.id)))}
              onChange={(_event, newValue) => setSelectedAgencies(newValue.map(val => String(val.id)))}
              renderInput={(params) => (
                <TextField {...params} label={t('dashboard.filters.agencies')} />
              )}
            />

            <Autocomplete
              multiple
              size="small"
              limitTags={2}
              options={teamOptions}
              getOptionLabel={(option) => option.name}
              value={teamOptions.filter((option) => selectedTeams.includes(String(option.id)))}
              onChange={(_event, newValue) => {
                const mappedValues = newValue.map(val => String(val.id));
                setSelectedTeams(mappedValues);
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('dashboard.filters.teams')} />
              )}
            />

            <Autocomplete
              multiple
              size="small"
              limitTags={2}
              options={channelOptions}
              getOptionLabel={(option) => option.name}
              value={channelOptions.filter((option) => selectedChannels.includes(String(option.id)))}
              onChange={(_event, newValue) => setSelectedChannels(newValue.map(val => String(val.id)))}
              renderInput={(params) => (
                <TextField {...params} label={t('dashboard.filters.channels')} />
              )}
            />

            <DatePicker
              label={t('dashboard.trends.startDate')}
              value={startDate}
              onChange={(newValue: Dayjs | null) => setStartDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />

            <DatePicker
              label={t('dashboard.trends.endDate')}
              value={endDate}
              onChange={(newValue: Dayjs | null) => setEndDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            fullWidth
          >
            {t('filters.clearAll')}
          </Button>
        </Box>
      </Popover>

      <Container maxWidth="xl">
        <Stack spacing={4}>
          <ImportantNumbersSection 
            importantNumbers={importantNumbers} 
            onRefresh={handleRefreshImportantNumbers}
          />
          <ComparativesSection
            casesByPeriods={casesByPeriods}
            casesResolvedByPeriods={casesResolvedByPeriods}
            casesNeedAgentByTimeOpen={casesNeedAgentByTimeOpen}
            selectedAgencies={selectedAgencies.map(id => Number(id))}
            startDate={startDate?.toISOString()}
            endDate={endDate?.toISOString()}
            onRefresh={handleRefreshComparatives}
          />
          <NeedsAgentSection
            needAgentStats={needAgentStats}
            onRefresh={handleRefreshNeedsAgent}
          />
          <AgentsStatusSection />
          <TrendsSection 
            startDate={startDate} 
            endDate={endDate} 
            casesByDayOfWeek={casesByDayOfWeek}
            casesByHourPattern={casesByDayAndHour}
            selectedDayOfWeek={selectedDayOfWeek}
            onDayOfWeekChange={setSelectedDayOfWeek}
            onRefresh={handleRefreshTrends}
          />
        </Stack>
      </Container>
    </Box>
  );
};
